import { useSearchParams } from 'react-router-dom';
import { INVITE_PARAM } from '../../lib/invite-links';
import { trpc } from '../../lib/trpc';

export type InviteSignup = {
  /** The token, if this page was reached from an invite link. */
  token: string | null;
  /** The invited address — the only email this sign-up/sign-in is allowed to use. */
  email: string | null;
  workspaceName: string | null;
  /** Details are still loading; the email field can't be locked to a value yet. */
  isLoading: boolean;
  /** A token was supplied but is invalid, expired, or already used. */
  isUnusable: boolean;
};

/**
 * Invite context for the auth pages. An invite names an exact address, so the register/login
 * forms lock the email field to it and hand the token to the backend, which rejects a
 * mismatched sign-up (including a Google account picked from the wrong identity) before any
 * account row is written. `token` is deliberately null while the details fail to load, so an
 * expired invite degrades to an ordinary open sign-up rather than a dead form.
 */
export function useInviteSignup(): InviteSignup {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(INVITE_PARAM);

  const { data, isLoading, error } = trpc.workspace.getInvitationDetails.useQuery(
    { token: token || '' },
    { enabled: Boolean(token), retry: false }
  );

  const isUnusable = Boolean(token) && Boolean(error);

  return {
    token: isUnusable ? null : token,
    email: data?.email ?? null,
    workspaceName: data?.workspaceName ?? null,
    isLoading: Boolean(token) && isLoading,
    isUnusable,
  };
}
